import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Brain, Check, Lock, Shield, Sparkles } from 'lucide-react';
import { authStaggerContainer, authStaggerItem } from './authMotion';
import { authPageStories, pathnameToAuthPageId } from './authPageStories';
import { AuthCashflowGraph } from './visuals/AuthCashflowGraph';
import { AuthAIInsightPanel } from './visuals/AuthAIInsightPanel';
import { AuthKpiRings } from './visuals/AuthKpiRings';
import { AuthOperationsFeed } from './visuals/AuthOperationsFeed';
import { cn } from '../../utils/cn';

const WORKSPACE_STEPS = [
  'Entity & workspace type',
  'Operational preferences',
  'AI calibration',
  'Dashboard launch',
];

const SECURITY_NODES = ['Identity verified', 'Encrypted channel', 'Reset issued', 'Access restored'];

export const AuthStoryAside: React.FC = () => {
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();
  const pageId = pathnameToAuthPageId(pathname);
  const story = authPageStories[pageId];

  return (
    <motion.div
      key={pageId}
      className="mx-auto w-full max-w-[26rem] max-h-[calc(100dvh-3rem)] overflow-hidden"
      variants={authStaggerContainer}
      initial="hidden"
      animate="visible"
    >
      <motion.header variants={authStaggerItem} className="shrink-0 text-center">
        <img
          src="/cmndf-logo.png"
          alt="cmnd[f]."
          className="mx-auto h-9 w-auto object-contain"
          loading="eager"
        />
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-blue-600">
          {story.eyebrow}
        </p>
        <h2 className="mt-2 text-xl font-semibold leading-snug tracking-tight text-slate-900 lg:text-[1.35rem]">
          {story.headline}
        </h2>
        <p className="mt-2 line-clamp-2 text-sm leading-snug text-slate-600">{story.subtitle}</p>
        <motion.div
          className="mx-auto mt-3 flex w-fit max-w-full items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1"
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
          <span className="truncate text-[11px] font-semibold text-blue-700">{story.stat}</span>
        </motion.div>
      </motion.header>

      <motion.div variants={authStaggerItem} className="mt-4 shrink-0 overflow-hidden">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {story.asideVariant === 'command' && (
            <div>
              <div className="border-b border-slate-100 px-4 py-3">
                <AuthCashflowGraph compact />
              </div>
              <AuthAIInsightPanel compact />
              <AuthKpiRings compact />
              <AuthOperationsFeed compact />
            </div>
          )}

          {story.asideVariant === 'workspace' && (
            <div className="p-4">
              <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Onboarding timeline
              </p>
              <ol className="mt-2 shrink-0 space-y-2">
                {WORKSPACE_STEPS.map((step, i) => (
                  <li key={step} className="flex items-center gap-2.5 text-xs">
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                        i === 0 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                      )}
                    >
                      {i === 0 ? <Check className="h-3 w-3" /> : i + 1}
                    </span>
                    <span className="text-slate-700">{step}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                <AuthCashflowGraph compact />
              </div>
            </div>
          )}

          {story.asideVariant === 'security' && (
            <div className="p-4" aria-hidden>
              <div className="relative flex h-28 shrink-0 items-center justify-center rounded-lg border border-slate-100 bg-slate-50">
                <Shield className="h-10 w-10 text-blue-600/80" />
                {SECURITY_NODES.map((_, i) => (
                  <motion.span
                    key={i}
                    className="absolute h-2 w-2 rounded-full bg-blue-500/60"
                    style={{
                      left: `${20 + i * 18}%`,
                      top: `${30 + (i % 2) * 35}%`,
                    }}
                    animate={reduceMotion ? {} : { opacity: [0.3, 0.9, 0.3] }}
                    transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.4 }}
                  />
                ))}
              </div>
              <ul className="mt-3 shrink-0 space-y-1.5">
                {SECURITY_NODES.map((n) => (
                  <li key={n} className="flex items-center gap-2 text-[11px] text-slate-600">
                    <Lock className="h-3 w-3 shrink-0 text-blue-600" />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {story.asideVariant === 'integrity' && (
            <div className="p-4">
              <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Integrity systems
              </p>
              <div className="mt-2 shrink-0 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                <p className="text-xs font-medium text-emerald-800">Password policy active</p>
                <ul className="mt-1.5 space-y-0.5 text-[10px] text-emerald-700">
                  <li>Minimum entropy enforced</li>
                  <li>Breach database check</li>
                  <li>Session rotation on save</li>
                </ul>
              </div>
              <div className="mt-3 min-h-0 shrink-0">
                <AuthKpiRings compact />
              </div>
            </div>
          )}

          {story.asideVariant === 'activation' && (
            <div className="p-4">
              <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Activation sequence
              </p>
              <div className="relative my-2 flex h-24 shrink-0 items-center justify-center">
                <motion.div
                  className="h-16 w-16 rounded-full border-2 border-blue-200 bg-blue-50"
                  animate={reduceMotion ? {} : { scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <Sparkles className="absolute h-7 w-7 text-blue-600" />
              </div>
              <AuthAIInsightPanel compact />
            </div>
          )}

          <div className="flex shrink-0 items-center justify-center gap-2 border-t border-slate-100 bg-slate-50/50 px-4 py-2.5 text-center">
            <Brain className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />
            <p className="line-clamp-2 text-[10px] leading-snug text-slate-600">{story.trustLine}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
